import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, forwardRef, inject, input, output, signal } from '@angular/core';
import { NG_VALUE_ACCESSOR, type AbstractControl, type ValidationErrors } from '@angular/forms';
import { HxBaseControl } from '../../base/hx-base-control';
import { HxButton } from '../button/button';
import { type HxDialogSize } from '../dialog/dialog-config';
import { HxDialogService } from '../dialog/dialog.service';
import { HxIcon } from '../icon/icon';
import { HxIconPickerDialogComponent } from './icon-picker-dialog';
import type { HxIconCategory } from './icon-picker.models';

@Component({
  selector: 'hx-icon-picker',
  standalone: true,
  imports: [CommonModule, HxButton, HxIcon],
  templateUrl: './icon-picker.html',
  styleUrls: ['./icon-picker.css', './icon-picker-tokens.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => HxIconPicker),
      multi: true,
    },
  ],
  host: {
    class: 'hx-icon-picker',
    '[class.hx-icon-picker--disabled]': 'disabledState()',
  },
})
/**
 * Icon picker control that opens a dialog to browse, search, and select icons.
 *
 * Use this when you need a consistent, accessible way to let users choose a single icon from a curated catalog. It
 * supports grouped categories, search, empty states, and configurable dialog sizing to fit different surfaces.
 */
export class HxIconPicker extends HxBaseControl {
  private readonly dialogService = inject(HxDialogService);

  /** Icon categories to display, grouped by name and containing icon metadata. */
  readonly categories = input<HxIconCategory[]>([]);
  /** Dialog title text shown in the header. */
  readonly dialogTitle = input<string>('Add Icons');
  /** Placeholder text for the search input. */
  readonly searchPlaceholder = input<string>('Search Icons');
  /** Empty state message shown when no icons match the search. */
  readonly emptyStateText = input<string>('No icons found');
  /** Default icon name used when no selection exists. */
  readonly defaultIcon = input<string>('bounding-box');
  /** Whether the trigger shows the icon label next to the icon. */
  readonly showLabel = input<boolean>(true);

  /** Dialog size preset: 'small' | 'medium' | 'large' | 'xlarge' | 'fullscreen'. */
  readonly dialogSize = input<HxDialogSize>('large');
  /** Dialog width override (any valid CSS width value). */
  readonly dialogWidth = input<string | undefined>(undefined);
  /** Dialog height override (any valid CSS height value). */
  readonly dialogHeight = input<string | undefined>(undefined);
  /** Dialog maximum width (any valid CSS width value). */
  readonly dialogMaxWidth = input<string | undefined>(undefined);
  /** Dialog maximum height (any valid CSS height value). */
  readonly dialogMaxHeight = input<string | undefined>('80vh');
  /** Additional dialog panel class name(s) applied to the overlay panel. */
  readonly dialogPanelClass = input<string | string[] | undefined>(undefined);

  readonly valueChange = output<string>();

  protected readonly _value = signal<string | null>(null);

  protected readonly displayIcon = computed(() => {
    return this._value() || this.defaultIcon();
  });

  protected readonly displayLabel = computed(() => {
    return this._value() || this.defaultIcon();
  });

  openPicker(): void {
    if (this.disabledState()) {
      return;
    }

    const dialogRef = this.dialogService.open(HxIconPickerDialogComponent, {
      size: this.dialogSize(),
      width: this.dialogWidth(),
      height: this.dialogHeight(),
      maxWidth: this.dialogMaxWidth(),
      maxHeight: this.dialogMaxHeight(),
      panelClass: this.buildPanelClass(),
      ariaLabel: this.dialogTitle(),
      inputs: {
        title: this.dialogTitle(),
        searchPlaceholder: this.searchPlaceholder(),
        emptyStateText: this.emptyStateText(),
        categories: this.categories(),
        selectedIcon: this._value(),
        defaultIcon: this.defaultIcon(),
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (typeof result === 'string') {
        this._value.set(result);
        this.valueChange.emit(result);
        this.notifyChange(result);
      }
      this.markTouched();
    });
  }

  protected override _setValue(value: string | null | undefined): void {
    this._value.set(value ?? null);
  }

  protected override _validate(_control: AbstractControl): ValidationErrors | null {
    return null;
  }

  private buildPanelClass(): string | string[] {
    const userClass = this.dialogPanelClass();
    const base = 'hx-icon-picker-panel';
    if (!userClass) return base;
    if (Array.isArray(userClass)) return [base, ...userClass];
    return [base, userClass];
  }
}
